import { ConfirmationModal, FormModal as BaseFormModal, Loader } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { getDirtyFields, splitDirtyArrayToStatus } from '@ubeya/shared/utils/form';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from 'react-query';
import { media } from '@ubeya/shared-web/style';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import * as api from '@ubeya/shared/services/api';
import SubLocationsSection from './SubLocationsSection';
import LocationFields from './LocationFields';

const FormModal = styled(BaseFormModal)`
  width: 80vw;
  max-height: 90vh;
  height: unset;
  min-height: 60vh;

  ${media.mobile`
      max-width: 600px;
    `}
`;

const formatSubLocations = (subLocations = []) =>
  subLocations.length > 0
    ? subLocations.map(({ location, photo, instructions, attachments = [], ...rest }) => ({
        ...rest,
        // Spread the location to get lat lng as part of the object
        ...location,
        ...(attachments.length > 0 ? { attachments: splitDirtyArrayToStatus('attachments', { attachments }) } : {}),
        instructions: instructions || '',
        photo: photo || undefined
      }))
    : undefined;

const LocationModal = ({
  selectedLocation,
  onClose,
  isLoadingSelectedLocation,
  onConfirm,
  showBranchSelector = false,
  editableAddress = false,
  disableFieldsEdit = false,
  disableBranchEdit = false,
  branchesOptions = [],
  className,
  showOrgAssignments = false
}) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const queryClient = useQueryClient();
  const [showPromptBeforeUpdate, setShowPromptBeforeUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subLocationsChanged, setSubLocationsChanged] = useState(false);
  const subLocationsRef = useRef();

  const initialValues = useMemo(
    () => ({
      ...selectedLocation,
      orgVenueId: selectedLocation?.orgVenueId ?? null,
      orgContractId: selectedLocation?.orgContractId ?? null,
      photo: selectedLocation?.photo || selectedLocation?.image
    }),
    [selectedLocation]
  );

  //scroll to bottom when updating sub locations
  useEffect(() => {
    if (!subLocationsChanged) {
      return;
    }
    subLocationsRef && subLocationsRef.current && subLocationsRef.current.scrollIntoView({ behavior: 'smooth' });

    setSubLocationsChanged(false);
  }, [subLocationsChanged]);

  const persistOrgTypes = useCallback(
    async ({ values, dirtyFields, createdLocationId }) => {
      if (!showOrgAssignments) {
        return;
      }

      const promises = [];

      // an existing location only needs a call when the venue actually changed; a just-created
      // one has no stored venue yet, so any chosen venue must be sent
      const locationId = selectedLocation?.id ?? createdLocationId;
      const shouldSetVenue = selectedLocation?.id ? !!dirtyFields.orgVenueId : !!values.orgVenueId;
      const shouldSetContract = selectedLocation?.id ? !!dirtyFields.orgContractId : !!values.orgContractId;

      if (locationId && shouldSetVenue) {
        promises.push(
          api.setLocationOrgVenue({
            accountId,
            locationId,
            orgVenueId: values.orgVenueId ?? null
          })
        );
      }

      if (locationId && shouldSetContract) {
        promises.push(
          api.setLocationOrgContract({
            accountId,
            locationId,
            orgContractId: values.orgContractId ?? null
          })
        );
      }

      const initialAreaTypeById = (initialValues?.subLocations || []).reduce(
        (prev, { id, orgAreaTypeId }) => ({ ...prev, [id]: orgAreaTypeId ?? null }),
        {}
      );

      (values.subLocations || []).forEach(({ id, orgAreaTypeId, status }) => {
        // Only persisted areas can be assigned a type here; newly-added areas have
        // temporary negative ids and no server id to target yet
        if (status === 'deleted' || !(id > 0)) {
          return;
        }
        const nextType = orgAreaTypeId ?? null;
        if (nextType !== (initialAreaTypeById[id] ?? null)) {
          promises.push(api.setAreaOrgType({ accountId, areaId: id, orgTypeId: nextType }));
        }
      });

      if (promises.length === 0) {
        return;
      }

      await Promise.all(promises);

      await Promise.all([
        queryClient.invalidateQueries(['locations', accountId]),
        // the edit form reads the single-location query, so it must be refreshed too or
        // reopening the location shows a stale (empty) venue
        queryClient.invalidateQueries(['sub_locations', accountId]),
        queryClient.invalidateQueries(['orgTypes', accountId]),
        queryClient.invalidateQueries(['orgVenues', accountId]),
        queryClient.invalidateQueries(['orgContracts', accountId]),
        queryClient.invalidateQueries(['orgAccounts'])
      ]);
    },
    [accountId, initialValues?.subLocations, queryClient, selectedLocation?.id, showOrgAssignments]
  );

  const submitLocation = useCallback(
    async ({ values, form }) => {
      setIsLoading(true);
      const { dirtyFields } = form.getState();
      const dirtyValues = getDirtyFields(values, dirtyFields);
      const { name, address, location, photo, subLocations } = dirtyValues;
      const { lat, lng } = location || {};

      const addMode = !initialValues?.address;
      const formattedSubLocations = subLocations
        ? {
            added: formatSubLocations(subLocations.added),
            edited: formatSubLocations(subLocations.edited),
            deleted: (subLocations?.deleted || []).length === 0 ? undefined : subLocations.deleted
          }
        : undefined;

      try {
        const saved = await onConfirm({
          name: addMode ? values.name : name,
          address,
          photo,
          lat,
          lng,
          // instructions: instructions || '',
          // attachments: splitDirtyArrayToStatus('attachments', { attachments }),
          branchId: showBranchSelector ? values.branchId : undefined,
          subLocations: formattedSubLocations
        });

        await persistOrgTypes({ values, dirtyFields, createdLocationId: saved?.data?.id });

        onClose();
        // eslint-disable-next-line no-empty
      } catch (e) {}

      setIsLoading(false);
    },
    [initialValues?.address, onClose, onConfirm, persistOrgTypes, showBranchSelector]
  );

  const handleSubmit = async (values, form) => {
    const { dirtyFields } = form.getState();

    // eslint-disable-next-line unused-imports/no-unused-vars
    const { subLocations, ...showPromptDirtyFields } = dirtyFields;

    if (selectedLocation?.id && Object.entries(showPromptDirtyFields)?.length > 0) {
      setShowPromptBeforeUpdate({ values, form });
    } else {
      await submitLocation({ values, form });
    }
  };

  return (
    <FormModal
      className={className}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      size="sLong"
      loadingConfirm={isLoading}
      onClose={onClose}
      title={selectedLocation?.id ? t('editLocation') : t('addLocation')}
      confirmText={selectedLocation?.id ? t('update') : t('add')}
      showCancelButton
      keepDirtyOnReinitialize
      disabledOnPristine={false}
      $showBranchSelector={showBranchSelector}>
      {isLoadingSelectedLocation ? (
        <Loader />
      ) : (
        <>
          <LocationFields
            showOrgAssignments={showOrgAssignments}
            {...{
              showBranchSelector,
              editableAddress,
              disableFieldsEdit,
              disableBranchEdit,
              branchesOptions,
              selectedLocation
            }}
          />
          <SubLocationsSection setSubLocationsChanged={setSubLocationsChanged} />
          <div ref={subLocationsRef} />
        </>
      )}
      {showPromptBeforeUpdate && (
        <ConfirmationModal
          onClose={() => setShowPromptBeforeUpdate(false)}
          title={t('areYouSure')}
          body={t('areYouSureUpdateLocation')}
          onConfirm={() => {
            submitLocation(showPromptBeforeUpdate);
            setShowPromptBeforeUpdate(false);
          }}
          confirmText={t('ok')}
          showCancelButton
        />
      )}
    </FormModal>
  );
};

export default LocationModal;
